import axios from 'axios';

const PRODUCTION_API = 'https://cssrmsapi.casanteylabs.com';
const PRODUCTION_HOSTS = new Set(['cssrms.casanteylabs.com', 'www.cssrms.casanteylabs.com']);

// Use .env REACT_APP_API_URL or production URL. When served from production domain, always use production API.
function getApiBaseURL() {
  const fromEnv = process.env.REACT_APP_API_URL?.trim();
  if (fromEnv) return fromEnv.replace(/\/api\/?$/, '') || fromEnv;

  const hostname = typeof window !== 'undefined' ? window.location?.hostname : '';
  const isProductionHost = hostname && PRODUCTION_HOSTS.has(hostname);

  if (isProductionHost) return PRODUCTION_API;
  // For local/LAN access (e.g. 10.x.x.x), default to same host on backend port.
  if (hostname && hostname !== 'localhost' && hostname !== '127.0.0.1') {
    return `${window.location.protocol}//${hostname}:3000`;
  }
  return 'http://localhost:3000';
}

// Create Axios instance
const api = axios.create({
  baseURL: getApiBaseURL(),
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

// Request interceptor: Add Bearer token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor: Handle 401 â†’ refresh token
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const isAuthRequest =
      originalRequest?.url?.includes('/auth/login') ||
      originalRequest?.url?.includes('/auth/request-otp');

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !isAuthRequest
    ) {
      originalRequest._retry = true;
      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) throw new Error('No refresh token');

        // Use same api instance for refresh (prevents infinite loop)
        const response = await api.post('/auth/refresh', { refreshToken });

        localStorage.setItem('accessToken', response.data.accessToken);
        originalRequest.headers.Authorization = `Bearer ${response.data.accessToken}`;

        return api(originalRequest);
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        window.location.href = '/';
      }
    }

    return Promise.reject(error);
  }
);

// API exports
export const authAPI = {
  login: async (credentials) => (await api.post('/auth/login', credentials)).data,
  requestOtp: async (phone) => (await api.post('/auth/request-otp', { phone })).data,
  forgotPassword: async (phone) => (await api.post('/auth/forgot-password', { phone })).data,
  setupPassword: async (data) => (await api.post('/auth/setup-password', data)).data,
  refreshToken: async (refreshToken) => (await api.post('/auth/refresh', { refreshToken })).data,
  logout: async () => (await api.post('/auth/logout')).data,
};

export const usersAPI = {
  getAll: async () => (await api.get('/users')).data,
  getById: async (id) => (await api.get(`/users/${id}`)).data,
  update: async (id, data) => (await api.put(`/users/${id}`, data)).data,
};

export const propertiesAPI = {
  getAll: async (params = {}) => (await api.get('/properties', { params })).data,
  getById: async (id) => (await api.get(`/properties/${id}`)).data,
  getByBillNo: async (billNo) => (await api.get(`/properties/bill/${billNo}`)).data,
  getAssignedToAgent: async (agentId) => (await api.get(`/properties/agent/${agentId}`)).data,
  create: async (data) => (await api.post('/properties', data)).data,
  update: async (id, data) => (await api.put(`/properties/${id}`, data)).data,
  delete: async (id) => (await api.delete(`/properties/${id}`)).data,
  assignToAgent: async (propertyId, agentId) =>
    (await api.post(`/properties/${propertyId}/assign/${agentId}`)).data,
  unassign: async (propertyId) =>
    (await api.post(`/properties/${propertyId}/unassign`)).data,
};

export const agentsAPI = {
  getAll: async () => (await api.get('/agents')).data,
  getById: async (id) => (await api.get(`/agents/${id}`)).data,
  getPerformance: async (id) => (await api.get(`/agents/${id}/performance`)).data,
  getLeaderboard: async () => (await api.get('/agents/leaderboard/top')).data,
  getAssignedBills: async (agentId, search = '') =>
    (await api.get(`/agents/${agentId}/bills`, { params: { search } })).data,
  getBillDetails: async (agentId, valuationNo) =>
    (await api.get(`/agents/${agentId}/bills/${valuationNo}`)).data,
  getBillsByCollectionUnit: async (agentId, collectionUnit) =>
    (() => {
      const normalize = (b) => ({
        id: b.id,
        // Normalize to the same shape as /agents/:id/bills so UI can reuse components.
        valuationNo: b.valuationNo ?? b.valuation_no ?? b.billNo ?? b.bill_no,
        billNo: b.billNo ?? b.bill_no ?? null,
        ownerName:
          [b.first_name, b.surname].filter(Boolean).join(' ').trim() ||
          b.ownerName ||
          b.owner_name ||
          null,
        location: b.location ?? b.prop_address ?? b.address ?? null,
        totalDue: b.totalDue ?? b.total_due ?? b.total_amount ?? 0,
        currentAmount: b.currentAmount ?? b.current_amount ?? 0,
        // Collection-unit endpoint doesn't return status; these are â€œto deliverâ€ items.
        status: b.status ?? 'pending',
        dueDate: b.dueDate ?? b.due_date ?? null,
        gpsLat: b.gpsLat ?? b.gps_lat ?? b.latitude ?? null,
        gpsLng: b.gpsLng ?? b.gps_lng ?? b.longitude ?? null,
        collectionUnit: b.collectionUnit ?? b.collection_unit ?? null,
      });

      return api
        .get(`/agents/${agentId}/collection-units/bills`, {
          params: { collection_unit: collectionUnit },
        })
        .then((res) => (Array.isArray(res.data) ? res.data.map(normalize) : res.data));
    })(),
};

export const adminAPI = {
  createAgent: async (data) => (await api.post('/admin/agents', data)).data,
  createSupervisor: async (data) => (await api.post('/admin/supervisors', data)).data,
  createExternalUser: async (data) => (await api.post('/admin/external-users', data)).data,
  getSupervisors: async () => (await api.get('/admin/supervisors')).data,
  getSupervisorAgents: async (supervisorId) =>
    (await api.get(`/admin/supervisors/${supervisorId}/agents`)).data,
  assignAgentsToSupervisor: async (supervisorId, agentIds) =>
    (await api.post(`/admin/supervisors/${supervisorId}/agents`, { agentIds })).data,
  unassignAllAgentsFromSupervisor: async (supervisorId) =>
    (await api.post(`/admin/supervisors/${supervisorId}/agents/unassign`)).data,
  assignProperties: async (agentId, propertyIds) =>
    (await api.post(`/admin/agents/${agentId}/assign`, { propertyIds })).data,
  unassignAllFromAgent: async (agentId) =>
    (await api.post(`/admin/agents/${agentId}/unassign-all`)).data,
  getUnassignedProperties: async () => (await api.get('/admin/properties/unassigned')).data,
  getCollectionUnits: async () => (await api.get('/admin/collection-units')).data,
  getCollectionUnitProperties: async (unit) => (await api.get('/admin/collection-units/' + unit + '/properties')).data,
  importPaymentsExcel: async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return (await api.post('/admin/payments/import-excel', fd)).data;
  },
  assignCollectionUnits: async (agentId, collectionUnits) =>
    (await api.put(`/admin/agents/${agentId}/collection-units`, { collectionUnits })).data,
  getDashboardStats: async () => (await api.get('/admin/dashboard')).data,
  getAllPermissions: async () => (await api.get('/admin/permissions')).data,
  updateAgentPermissions: async (agentId, permissions) =>
    (await api.put(`/admin/agents/${agentId}/permissions`, { permissions })).data,
  updateAgentTreatment: async (agentId, treatmentCategory) =>
    (await api.put(`/admin/agents/${agentId}/treatment`, { treatmentCategory })).data,
  getUser: async (id) => (await api.get(`/admin/users/${id}`)).data,
  updateUser: async (id, data) => (await api.patch(`/admin/users/${id}`, data)).data,
  deleteUser: async (id) => (await api.delete(`/admin/users/${id}`)).data,
};

export const supervisorsAPI = {
  getMyAgents: async () => (await api.get('/supervisors/me/agents')).data,
  getAgentActivity: async (agentId) => (await api.get(`/supervisors/agents/${agentId}/activity`)).data,
};

export const deliveriesAPI = {
  create: async (data) => (await api.post('/deliveries', data)).data,
  getAll: async (params = {}) => (await api.get('/deliveries', { params })).data,
  getByProperty: async (propertyId) =>
    (await api.get(`/deliveries/property/${propertyId}`)).data,
};

export const paymentsAPI = {
  create: async (data) => (await api.post('/payments', data)).data,
  getAll: async (params = {}) => (await api.get('/payments', { params })).data,
  verify: async (paymentId) => (await api.post(`/payments/${paymentId}/verify`)).data,
};

export default api;
export const reportsAPI = {
  getCollectionUnitsMeta: async () => (await api.get('/reports/meta/collection-units')).data,
  payments: async (params = {}) => (await api.get('/reports/payments', { params })).data,
  deliveries: async (params = {}) => (await api.get('/reports/deliveries', { params })).data,
  deliverySummary: async (params = {}) => (await api.get('/reports/delivery-summary', { params })).data,
  paymentSummary: async (params = {}) => (await api.get('/reports/payment-summary', { params })).data,
};
